/**
 * Text Normalization Utilities
 * Functions to normalize special characters and smart quotes for consistent text handling
 */

/**
 * Normalizes smart quotes and other special characters to prevent encoding issues
 * @param {string} text - The text to normalize
 * @returns {string} - Normalized text with regular characters
 */
export const normalizeText = (text) => {
  if (!text) return text;
  
  return text
    // Replace smart quotes with regular quotes (comprehensive list)
    .replace(/[''′]/g, "'")  // Smart single quotes and prime to regular single quote
    .replace(/[""″]/g, '"')  // Smart double quotes and double prime to regular double quotes
    .replace(/[''′]/g, "'")  // Left smart single quotes to regular single quote
    .replace(/[''′]/g, "'")  // Right smart single quotes to regular single quote
    .replace(/[""″]/g, '"')  // Left smart double quotes to regular double quotes
    .replace(/[""″]/g, '"')  // Right smart double quotes to regular double quotes
    
    // Replace smart apostrophes and other problematic characters
    .replace(/[''′]/g, "'")  // Smart apostrophes to regular apostrophe
    .replace(/[''′]/g, "'")  // Alternative smart apostrophes to regular apostrophe
    
    // Replace other common smart characters
    .replace(/…/g, '...')   // Ellipsis to three dots
    .replace(/–/g, '-')     // En dash to regular dash
    .replace(/—/g, '-')     // Em dash to regular dash
    .replace(/•/g, '*')     // Bullet to asterisk
    .replace(/°/g, ' degrees ') // Degree symbol to text
    .replace(/©/g, '(c)')   // Copyright to (c)
    .replace(/®/g, '(R)')   // Registered trademark to (R)
    .replace(/™/g, '(TM)')  // Trademark to (TM)
    
    // Replace other problematic Unicode characters
    .replace(/[\u2018\u2019\u2032]/g, "'")  // Unicode smart quotes and primes
    .replace(/[\u201C\u201D\u2033]/g, '"')  // Unicode smart double quotes
    .replace(/[\u2013\u2014]/g, '-')        // Unicode dashes
    .replace(/[\u2022\u2023]/g, '*')        // Unicode bullets
    .replace(/[\u2026]/g, '...')            // Unicode ellipsis
    .replace(/[\u00A9]/g, '(c)')            // Unicode copyright
    .replace(/[\u00AE]/g, '(R)')            // Unicode registered
    .replace(/[\u2122]/g, '(TM)');          // Unicode trademark
};

/**
 * Normalizes text for API submission to prevent encoding issues
 * @param {string} text - The text to normalize for API
 * @returns {string} - API-safe normalized text
 */
export const normalizeTextForAPI = (text) => {
  if (!text) return text;
  
  return normalizeText(text)
    // Additional API-specific normalizations
    .replace(/\s+/g, ' ')  // Normalize multiple spaces to single space
    .trim();               // Remove leading/trailing whitespace
};

/**
 * Handles paste events to normalize pasted content
 * @param {Event} event - The paste event
 * @param {string} currentValue - Current text value
 * @param {Function} updateFunction - Function to update the text value
 */
export const handlePasteNormalization = (event, currentValue, updateFunction) => {
  const pastedText = event.clipboardData.getData('text');
  const normalizedText = normalizeTextComprehensive(pastedText);
  
  if (pastedText !== normalizedText) {
    event.preventDefault();
    const newValue = currentValue + normalizedText;
    updateFunction(newValue);
  }
};

/**
 * Comprehensive text normalization for all problematic characters
 * This function handles the specific cases mentioned in the user's example
 * @param {string} text - The text to normalize
 * @returns {string} - Fully normalized text
 */
export const normalizeTextComprehensive = (text) => {
  if (!text) return text;
  
  return text
    // Handle all types of smart quotes and apostrophes
    .replace(/[\u2018\u2019\u2032\u02BC\u02B9]/g, "'")  // All smart single quotes and apostrophes
    .replace(/[\u201C\u201D\u2033\u02DD]/g, '"')        // All smart double quotes
    
    // Handle specific problematic characters that convert to question marks
    .replace(/[\u2018\u2019]/g, "'")  // Left and right single quotation marks
    .replace(/[\u201C\u201D]/g, '"')  // Left and right double quotation marks
    .replace(/[\u2032\u2033]/g, "'")  // Prime and double prime
    
    // Handle other Unicode characters that might cause issues
    .replace(/[\u2013\u2014]/g, '-')  // En dash and em dash
    .replace(/[\u2022\u2023]/g, '*')  // Bullet points
    .replace(/[\u2026]/g, '...')      // Ellipsis
    .replace(/[\u00A9]/g, '(c)')      // Copyright
    .replace(/[\u00AE]/g, '(R)')      // Registered
    .replace(/[\u2122]/g, '(TM)')     // Trademark
    .replace(/[\u00B0]/g, ' degrees ') // Degree symbol
    .replace(/[\u00A0]/g, ' ')        // Non-breaking space to regular space
    .replace(/[\u200B\u200C\u200D]/g, '') // Zero-width characters
    .replace(/[\uFEFF]/g, '');        // Byte order mark
};

export default {
  normalizeText,
  normalizeTextForAPI,
  handlePasteNormalization,
  normalizeTextComprehensive
};
